/**
 * SimpleStockBoard — « Point de stock » du cuisinier en mode simple (Juin 2026)
 *
 * Ma liste de produits (reprise du dernier point) → grosses touches − / + → un seul bouton ENVOYER.
 * Le stock système n'est pas modifié : l'admin rapproche ensuite le rapport.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ClipboardCheck, Plus, Minus, Search, Send, X, CheckCircle2, Clock, PenSquare, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UNITS = ["portion", "kg", "g", "L", "cl", "unité", "pièce", "bouteille", "sachet", "boîte", "carton", "casier", "botte"];

const STATUS = {
  submitted: { label: "Envoyé · en attente", cls: "bg-amber-500/20 text-amber-200 border-amber-500/40", icon: Clock },
  correct: { label: "Validé par l'admin", cls: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40", icon: CheckCircle2 },
  reconciled: { label: "Rapproché", cls: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40", icon: CheckCircle2 },
  to_modify: { label: "À refaire", cls: "bg-rose-500/20 text-rose-200 border-rose-500/40", icon: PenSquare },
  discrepancies_review: { label: "Écarts à revoir", cls: "bg-orange-500/20 text-orange-200 border-orange-500/40", icon: PenSquare },
  archived: { label: "Archivé", cls: "bg-slate-500/20 text-slate-300 border-slate-500/40", icon: Clock },
};

const SimpleStockBoard = ({ currentUser }) => {
  const userId = currentUser?.id || currentUser?.username || "unknown";
  const userName = currentUser?.full_name || currentUser?.username || "Cuisinier";
  const listKey = `cuisine_stock_list_${userId}`;

  const [products, setProducts] = useState([]);
  const [myIds, setMyIds] = useState(() => { try { return JSON.parse(localStorage.getItem(listKey) || "[]"); } catch { return []; } });
  const [counts, setCounts] = useState({});
  const unitsKey = `cuisine_stock_units_${userId}`;
  const [units, setUnits] = useState(() => { try { return JSON.parse(localStorage.getItem(unitsKey) || "{}"); } catch { return {}; } });
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => { localStorage.setItem(listKey, JSON.stringify(myIds)); }, [myIds, listKey]);
  useEffect(() => { localStorage.setItem(unitsKey, JSON.stringify(units)); }, [units, unitsKey]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        axios.get(`${API}/stock/products`),
        axios.get(`${API}/field-stock/reports`, { params: { kind: "kitchen", user_id: userId, role: "cuisinier", include_archived: "true" } }),
      ]);
      const prods = (p.data?.products || p.data || []).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setProducts(prods);
      const list = r.data?.reports || [];
      setReports(list.slice(0, 10));
      if (list[0]) {
        const d = await axios.get(`${API}/field-stock/reports/${list[0].id}`);
        const items = d.data?.report?.items || d.data?.items || [];
        const c = {};
        const u = {};
        const ids = [];
        for (const it of items) {
          if (!it.product_id) continue;
          ids.push(it.product_id);
          if (it.counted_qty !== null && it.counted_qty !== undefined) c[it.product_id] = String(it.counted_qty);
          if (it.unit && it.system_unit && it.unit !== it.system_unit) u[it.product_id] = it.unit;
        }
        setCounts((prev) => ({ ...c, ...prev }));
        setUnits((prev) => ({ ...u, ...prev }));
        setMyIds((prev) => Array.from(new Set([...prev, ...ids])));
      }
    } catch { toast.error("Erreur de chargement du point de stock"); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const byId = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const myProducts = useMemo(() => myIds.map((id) => byId[id]).filter(Boolean), [myIds, byId]);
  const filledCount = myProducts.filter((p) => counts[p.id] !== undefined && counts[p.id] !== "").length;

  const setQty = (id, v) => setCounts((prev) => ({ ...prev, [id]: v }));
  const bump = (id, delta) => {
    const cur = parseFloat(counts[id] ?? "0") || 0;
    setQty(id, String(Math.max(0, Math.round((cur + delta) * 100) / 100)));
  };
  const removeFromList = (id) => {
    setMyIds((prev) => prev.filter((x) => x !== id));
    setCounts((prev) => { const n = { ...prev }; delete n[id]; return n; });
  };

  const send = async () => {
    const items = myProducts
      .filter((p) => counts[p.id] !== undefined && counts[p.id] !== "" && !Number.isNaN(parseFloat(counts[p.id])))
      .map((p) => ({ product_id: p.id, counted_qty: parseFloat(counts[p.id]), unit: units[p.id] || p.unit || "" }));
    if (items.length === 0) { toast.error("Saisissez au moins une quantité"); return; }
    if (!window.confirm(`Envoyer le point de stock à l'administrateur ?\n\n${items.length} produit(s) compté(s).`)) return;
    setSending(true);
    try {
      const params = new URLSearchParams({ x_user_id: userId, x_user_name: userName }).toString();
      await axios.post(`${API}/field-stock/reports?${params}`, { category_ids: [], items, notes: "", kind: "kitchen" });
      toast.success(`Point de stock envoyé · ${items.length} produit(s)`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setSending(false); }
  };

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => !myIds.includes(p.id) && (!q || (p.name || "").toLowerCase().includes(q))).slice(0, 30);
  }, [products, myIds, search]);

  return (
    <div className="space-y-4" data-testid="simple-stock-board">
      <div className="rounded-2xl border-2 border-amber-500/40 bg-slate-900 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-white font-black text-lg leading-tight flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-amber-400" /> Mon point de stock cuisine</p>
          <p className="text-slate-400 text-sm">{myProducts.length} produit{myProducts.length > 1 ? "s" : ""} dans ma liste · {filledCount} compté{filledCount > 1 ? "s" : ""}</p>
        </div>
        <button type="button" onClick={() => setAddOpen(true)} className="h-12 px-4 rounded-xl bg-slate-800 border-2 border-slate-600 hover:border-amber-400 text-white font-black flex items-center gap-2" data-testid="stock-add-product-btn">
          <Plus className="w-5 h-5" /> Ajouter un produit
        </button>
        <button type="button" onClick={load} disabled={loading} className="h-12 w-12 rounded-xl bg-slate-800 border-2 border-slate-600 text-slate-300 flex items-center justify-center" data-testid="stock-refresh">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
        </button>
      </div>

      {myProducts.length === 0 && !loading && (
        <div className="text-center py-14 text-slate-500" data-testid="stock-empty">
          <ClipboardCheck className="w-14 h-14 mx-auto mb-3 text-amber-400/50" />
          <p className="text-2xl font-black text-slate-300">Ma liste est vide</p>
          <p className="text-base mt-1">Appuyez sur « Ajouter un produit » pour choisir ce que vous comptez.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {myProducts.map((p) => {
          const v = counts[p.id] ?? "";
          return (
            <div key={p.id} className={`rounded-2xl border-2 bg-slate-900 p-3 flex items-center gap-3 ${v === "" ? "border-slate-700" : "border-emerald-500/40"}`} data-testid={`stock-row-${p.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-lg leading-tight truncate">{p.name}</p>
                <div>
                <select
                  value={units[p.id] || p.unit || "unité"}
                  onChange={(e) => setUnits((prev) => ({ ...prev, [p.id]: e.target.value }))}
                  className={`mt-0.5 h-7 rounded-md bg-slate-950 border text-xs font-bold uppercase tracking-widest px-1.5 outline-none ${units[p.id] && units[p.id] !== p.unit ? "border-amber-400 text-amber-300" : "border-slate-700 text-slate-400"}`}
                  title="Changer l'unité de comptage"
                  data-testid={`stock-unit-${p.id}`}
                >
                  {Array.from(new Set([p.unit || "unité", ...UNITS])).map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
                </div>
              </div>
              <button type="button" onClick={() => bump(p.id, -1)} className="w-12 h-12 rounded-xl bg-slate-800 border-2 border-slate-600 text-white flex items-center justify-center active:scale-95" data-testid={`stock-minus-${p.id}`}>
                <Minus className="w-6 h-6" strokeWidth={3} />
              </button>
              <input
                type="number" inputMode="decimal" step="any" min="0" value={v} placeholder="0"
                onChange={(e) => setQty(p.id, e.target.value)}
                className="w-20 h-12 rounded-xl bg-slate-950 border-2 border-slate-600 focus:border-amber-400 text-white text-2xl font-black font-mono text-center outline-none"
                data-testid={`stock-qty-${p.id}`}
              />
              <button type="button" onClick={() => bump(p.id, 1)} className="w-12 h-12 rounded-xl bg-amber-500 text-slate-950 flex items-center justify-center active:scale-95" data-testid={`stock-plus-${p.id}`}>
                <Plus className="w-6 h-6" strokeWidth={3} />
              </button>
              <button type="button" onClick={() => removeFromList(p.id)} className="w-8 h-8 rounded-full text-slate-500 hover:text-rose-300 hover:bg-rose-500/10 flex items-center justify-center" title="Retirer de ma liste" data-testid={`stock-remove-${p.id}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      {myProducts.length > 0 && (
        <button type="button" onClick={send} disabled={sending || filledCount === 0}
          className="w-full rounded-2xl bg-emerald-500 hover:bg-emerald-400 active:scale-[0.99] text-slate-950 py-5 text-xl font-black flex items-center justify-center gap-2 transition-all disabled:opacity-50 sticky bottom-3 shadow-2xl"
          data-testid="stock-send-btn">
          {sending ? <Loader2 className="w-6 h-6 animate-spin" /> : <Send className="w-6 h-6" strokeWidth={2.5} />}
          ENVOYER LE POINT DE STOCK ({filledCount})
        </button>
      )}

      {reports.length > 0 && (
        <div className="space-y-2" data-testid="stock-my-reports">
          <p className="text-[11px] uppercase tracking-widest font-black text-slate-500">Mes derniers envois</p>
          {reports.map((r) => {
            const st = STATUS[r.status] || STATUS.submitted;
            const Icon = st.icon;
            return (
              <div key={r.id} className="rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-2.5 flex items-center gap-3" data-testid={`stock-report-${r.id}`}>
                <span className="text-white font-bold">{new Date(r.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                <span className="text-slate-400 text-sm">{r.items_count ?? 0} produit{(r.items_count ?? 0) > 1 ? "s" : ""}</span>
                <span className={`ml-auto text-xs font-bold px-2.5 py-1 rounded-full border flex items-center gap-1 ${st.cls}`}><Icon className="w-3.5 h-3.5" /> {st.label}</span>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-slate-900 border-amber-500/40 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-amber-200 flex items-center gap-2"><Plus className="w-5 h-5" /> Ajouter à ma liste</DialogTitle>
            <DialogDescription className="text-slate-400">Cherchez un produit et touchez-le pour l'ajouter.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-3.5 text-slate-500" />
            <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tomate, poulet, riz…"
              className="w-full h-12 pl-11 pr-3 rounded-xl bg-slate-950 border-2 border-slate-600 focus:border-amber-400 text-white text-lg outline-none" data-testid="stock-search-input" />
          </div>
          <div className="max-h-80 overflow-y-auto space-y-1.5 pr-1">
            {searchResults.map((p) => (
              <button key={p.id} type="button" onClick={() => { setMyIds((prev) => [...prev, p.id]); toast.success(`${p.name} ajouté`); }}
                className="w-full text-left rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 px-4 py-3 flex items-center justify-between" data-testid={`stock-pick-${p.id}`}>
                <span className="font-bold text-base truncate">{p.name}</span>
                <span className="text-slate-400 text-xs uppercase ml-2 shrink-0">{p.unit}</span>
              </button>
            ))}
            {searchResults.length === 0 && <p className="text-slate-500 text-center py-6">Aucun produit trouvé</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SimpleStockBoard;
